/// <reference types="vite/client" />

import axios from "axios";
import { User } from '../types/user';
import { authService } from './auth';

// Cache for ngrok URL to avoid repeated fetches
let ngrokUrlCache: string | null = null;
let ngrokUrlCacheTime: number = 0;
const NGROK_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Function to fetch ngrok URL from backend
const fetchNgrokUrl = async (): Promise<string | null> => {
  // Skip ngrok URL fetching in development mode
  if (import.meta.env.DEV) {
    console.debug('Ngrok URL fetching skipped in development mode');
    return null;
  }
  
  try {
    // Check cache first
    const now = Date.now();
    if (ngrokUrlCache && (now - ngrokUrlCacheTime) < NGROK_CACHE_DURATION) {
      return ngrokUrlCache;
    }

    // Use the current API base URL to fetch ngrok URL
    const baseUrl = import.meta.env.VITE_API_URL;
    // Remove /api suffix if it exists to avoid double /api/api/
    const cleanBaseUrl = baseUrl.replace(/\/api$/, '');
    const response = await fetch(`${cleanBaseUrl}/api/ngrok-url`);
    
    if (!response.ok) {
      console.warn('Failed to fetch ngrok URL:', response.status);
      return null;
    }
    
    const data = await response.json();
    
    // Check if ngrok is enabled and URL is valid
    if (data.enabled && data.ngrok_url && data.ngrok_url !== 'null') {
      ngrokUrlCache = data.ngrok_url;
      ngrokUrlCacheTime = now;
      
      // Expose ngrok URL cache to window for ApiService to access
      if (import.meta.env.DEV) {
        (window as any).__NGROK_URL_CACHE__ = data.ngrok_url;
      }
      
      console.log('Using ngrok URL:', data.ngrok_url);
      return data.ngrok_url;
    }
    
    return null;
  } catch (error) {
    console.warn('Error fetching ngrok URL:', error);
    return null;
  }
};

// Define the base URL for the API.
// Use environment variables to get the proper backend URL
const getApiBaseUrl = async (): Promise<string> => {
  const devUrl = import.meta.env.VITE_API_URL;
  const stagingUrl = import.meta.env.VITE_API_URL;
  const productionUrl = import.meta.env.VITE_API_URL;
  
  // Environment-specific URLs for staging/production
  if (import.meta.env.VITE_ENV === 'staging') {
    return stagingUrl;
  }
  
  if (import.meta.env.VITE_ENV === 'production') {
    return productionUrl;
  }
  
  // Development - try to get ngrok URL first
  if (import.meta.env.DEV) {
    // Check if ngrok URL detection is enabled
    if (import.meta.env.VITE_USE_NGROK === 'true') {
      const ngrokUrl = await fetchNgrokUrl();
      if (ngrokUrl) {
        return ngrokUrl;
      }
    }
    
    // Fallback to Vite proxy
    return devUrl; // This will be proxied by Vite
  }
  
  // Final fallback
  return devUrl;
};

// Create a synchronous version for immediate use
const getApiBaseUrlSync = (): string => {
  const devUrl = import.meta.env.VITE_API_URL;
  const stagingUrl = import.meta.env.VITE_API_URL;
  const productionUrl = import.meta.env.VITE_API_URL;
  
  // Environment-specific URLs for staging/production
  if (import.meta.env.VITE_ENV === 'staging') {
    return stagingUrl;
  }
  
  if (import.meta.env.VITE_ENV === 'production') {
    return productionUrl;
  }
  
  // Development - use cached ngrok URL or fallback
  if (import.meta.env.DEV) {
    if (ngrokUrlCache) {
      return ngrokUrlCache;
    }
    return devUrl; // This will be proxied by Vite
  }
  
  // Final fallback
  return devUrl;
};

// Initialize API base URL
let API_BASE_URL = getApiBaseUrlSync();

// Update API base URL asynchronously if needed
getApiBaseUrl().then(url => {
  API_BASE_URL = url;
  console.log('API Base URL set to:', API_BASE_URL);
}).catch(error => {
  console.warn('Failed to get dynamic API URL:', error);
});

// Base API client with authentication
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // Increased from 10000 to 15000ms
});

// Expose API client to window for debugging (development only)
if (import.meta.env.DEV) {
  (window as any).__API_CLIENT__ = apiClient;
}

// Function to update the API base URL (useful when ngrok URL changes)
export const updateApiBaseUrl = (newBaseUrl: string) => {
  API_BASE_URL = newBaseUrl;
  apiClient.defaults.baseURL = newBaseUrl;
  console.log('API Base URL updated to:', newBaseUrl);
  
  // Update window reference if in development
  if (import.meta.env.DEV) {
    (window as any).__API_CLIENT__ = apiClient;
    (window as any).__NGROK_URL_CACHE__ = newBaseUrl;
  }
};

// Function to refresh ngrok URL and update API client
export const refreshNgrokUrl = async () => {
  if (import.meta.env.DEV && import.meta.env.VITE_USE_NGROK === 'true') {
    const ngrokUrl = await fetchNgrokUrl();
    if (ngrokUrl) {
      updateApiBaseUrl(ngrokUrl);
      return ngrokUrl;
    }
  }
  return null;
};

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Ensure headers object exists
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Clear auth token and redirect to login
      localStorage.removeItem("access_token");
      window.location.href = "/login";
    }
    // Handle 403 Forbidden errors (role-based access)
    if (error.response?.status === 403) {
      console.error('Access forbidden: User does not have required role');
      // You can redirect to appropriate dashboard or show error message
    }
    return Promise.reject(error);
  }
);

// Role-based API service
class RoleBasedApiService {
  constructor() {
    // No need for complex URL resolution - use the apiClient that's already configured
  }

  // Get the appropriate API prefix based on user role
  private getApiPrefix(user: User): string {
    const primaryRole = authService.getPrimaryRole(user);
    
    switch (primaryRole) {
      case 'admin':
        return '/admin';
      case 'operator':
        return '/operator';
      case 'guest':
      default:
        return '/user';
    }
  }

  // Make authenticated API request with role-based endpoint
  async request<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string,
    data?: any,
    options?: {
      useRolePrefix?: boolean;
      user?: User;
      isPublic?: boolean;
    }
  ): Promise<T> {
    let url: string;

    // Handle public endpoints (no role prefix)
    if (options?.isPublic) {
      url = endpoint;
    } else {
      // Get current user for role-based routing
      // IMPORTANT: Always pass user from options to avoid circular dependency
      const user = options?.user;
      if (!user) {
        throw new Error('User object is required for role-based API calls. Pass user in options to avoid circular dependency.');
      }
      const prefix = this.getApiPrefix(user);
      url = `${prefix}${endpoint}`;
    }

    const config = {
      method,
      url,
      data,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    };

    // Retry logic for failed requests
    const maxRetries = 2;
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiClient.request(config);
        return response.data;
      } catch (error: any) {
        lastError = error;
        
        // Don't retry on certain error types
        if (error.response?.status === 401 || error.response?.status === 403 || error.response?.status === 404) {
          break;
        }
        
        // Retry on timeout or network errors
        if (attempt < maxRetries && (error.code === 'ECONNABORTED' || error.message.includes('timeout') || !error.response)) {
          // Only log warnings in development mode to reduce console noise
          if (import.meta.env.DEV) {
            console.warn(`API request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`, error.message);
          }
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
          continue;
        }
        
        break;
      }
    }

    // Handle final error
    if (lastError.response?.status === 403) {
      throw new Error('Access denied: You do not have permission to access this resource');
    }
    throw lastError;
  }

  // Get current user
  async getCurrentUser(): Promise<User> {
    return this.request<User>('GET', '/auth/user');
  }

  // Admin endpoints
  async getAdminUsers(params?: any): Promise<any> {
    return this.request('GET', '/users', undefined, { user: { role: 'admin' } as User });
  }

  async getAdminOperators(params?: any): Promise<any> {
    return this.request('GET', '/operators', undefined, { user: { role: 'admin' } as User });
  }

  async getAdminLocations(params?: any): Promise<any> {
    return this.request('GET', '/locations', undefined, { user: { role: 'admin' } as User });
  }

  async getAdminServices(params?: any): Promise<any> {
    return this.request('GET', '/services', undefined, { user: { role: 'admin' } as User });
  }

  async getAdminBookings(params?: any): Promise<any> {
    return this.request('GET', '/bookings', undefined, { user: { role: 'admin' } as User });
  }

  // Operator endpoints
  async getOperatorLocations(params?: any): Promise<any> {
    return this.request('GET', '/locations', undefined, { user: { role: 'operator' } as User });
  }

  async getOperatorServices(params?: any): Promise<any> {
    return this.request('GET', '/services', undefined, { user: { role: 'operator' } as User });
  }

  async getOperatorBookings(params?: any): Promise<any> {
    return this.request('GET', '/bookings', undefined, { user: { role: 'operator' } as User });
  }

  async getOperatorDashboardData(): Promise<any> {
    return this.request('GET', '/dashboard', undefined, { user: { role: 'operator' } as User });
  }

  async getOperatorProfile(): Promise<any> {
    return this.request('GET', '/profile', undefined, { user: { role: 'operator' } as User });
  }

  async updateOperatorProfile(data: any): Promise<any> {
    return this.request('PUT', '/profile', data, { user: { role: 'operator' } as User });
  }

  // Operator claim endpoint
  async claimOperatorListing(data: { email: string; code?: string }): Promise<any> {
    // This endpoint is public, so do not use role prefix
    return this.request('POST', '/operators/claim', data, { isPublic: true });
  }

  // Operator notifications endpoint
  async getOperatorNotifications(): Promise<any> {
    return this.request('GET', '/operators/me/notifications', undefined, { user: { role: 'operator' } as User });
  }

  // User endpoints
  async getUserBookings(params?: any): Promise<any> {
    return this.request('GET', '/bookings', undefined, { user: { role: 'guest' } as User });
  }

  async getUserProfile(): Promise<any> {
    return this.request('GET', '/auth/user', undefined, { isPublic: true });
  }

  async updateUserProfile(data: any): Promise<any> {
    return this.request('PUT', '/profile', data, { user: { role: 'guest' } as User });
  }

  async updateUserPassword(data: any): Promise<any> {
    return this.request('PUT', '/profile/password', data, { user: { role: 'guest' } as User });
  }

  async updateUserAvatar(data: FormData): Promise<any> {
    return this.request('POST', '/profile/avatar', data, { user: { role: 'guest' } as User });
  }

  async getUserPreferences(): Promise<any> {
    return this.request('GET', '/profile/preferences', undefined, { user: { role: 'guest' } as User });
  }

  async updateUserPreferences(data: any): Promise<any> {
    return this.request('POST', '/profile/preferences', data, { user: { role: 'guest' } as User });
  }

  // Public/shared endpoints
  async getBlogs(params?: any): Promise<any> {
    return this.request('GET', '/blogs', undefined, { isPublic: true });
  }

  async getContactForm(data: any): Promise<any> {
    return this.request('POST', '/contact', data, { isPublic: true });
  }

  // Generic methods for any endpoint
  async get<T = any>(endpoint: string, options?: any): Promise<T> {
    return this.request<T>('GET', endpoint, undefined, options);
  }

  async post<T = any>(endpoint: string, data?: any, options?: any): Promise<T> {
    return this.request<T>('POST', endpoint, data, options);
  }

  async put<T = any>(endpoint: string, data?: any, options?: any): Promise<T> {
    return this.request<T>('PUT', endpoint, data, options);
  }

  async delete<T = any>(endpoint: string, options?: any): Promise<T> {
    return this.request<T>('DELETE', endpoint, undefined, options);
  }

  async patch<T = any>(endpoint: string, data?: any, options?: any): Promise<T> {
    return this.request<T>('PATCH', endpoint, data, options);
  }

  // Operator preview endpoint
  async getOperatorPreview(): Promise<any> {
    return this.request('GET', '/operators/me/preview', undefined, { user: { role: 'operator' } as User });
  }

  // Public operator preview endpoints
  async getOperatorPreviewByToken(token: string): Promise<any> {
    return this.request('GET', `/operators/preview/${token}`, undefined, { isPublic: true });
  }

  async makeOperatorDecision(token: string, decision: 'yes' | 'no'): Promise<any> {
    return this.request('POST', `/operators/preview/${token}/decision`, { decision }, { isPublic: true });
  }

  async claimOperatorRequest(email: string, businessName: string): Promise<any> {
    return this.request('POST', '/operators/claim-request', { email, business_name: businessName }, { isPublic: true });
  }
}

const apiService = new RoleBasedApiService();

export { apiService, apiClient };
export default apiClient; 